/**
 * Order Log — export mapping (ORD-1.2 / ORD-1.3 / ORD-1.4)
 * The export mirrors the columns on screen, in the same order, under the active
 * sort and filters — so Table No. lands before Guests and the financial columns
 * follow the same waterfall the table shows.
 */
import { formatMoneyForExport } from '../../../../utils/currency';
import type { OrderLogColumnDef, OrderLogRow, OrderLogStatus } from './orderLogModel';
import { rowValue, buildOrderLogColumns } from './orderLogModel';
// The table's own footer maths, so the file's totals can never disagree with the screen's.
import { computeOrderLogTotals } from './OrderLogTable';

const DASH = '-';

function cell(row: OrderLogRow, col: OrderLogColumnDef): string {
    if (col.key === 'tableName') return row.tableList.length ? row.tableList.join(', ') : DASH;
    if (col.key === 'customerName') return row.customerName ?? 'Walk-in';
    if (col.key === 'guests') return row.guests ? String(row.guests) : '';
    if (col.numeric) {
        const v = rowValue(row, col.key) as number | null;
        if (v === null || v === undefined) return DASH;
        return col.reduction && v > 0 ? `-${formatMoneyForExport(v)}` : formatMoneyForExport(v);
    }
    const v = rowValue(row, col.key);
    return v === null || v === undefined || v === '' ? DASH : String(v);
}

/**
 * The table's footer row, as export cells. Summed over the rows being exported —
 * the whole filtered result set, not the page on screen — so the figure reads
 * "Total (27 orders)" where the screen's own footer, covering one loaded page,
 * says "Total (10 orders)". A column with no data on some orders sums the rest,
 * the way the footer's own tooltip explains on screen.
 */
function totalsRow(rows: OrderLogRow[], columns: OrderLogColumnDef[]): (string | number)[] {
    const totals = computeOrderLogTotals(rows, columns);
    return columns.map((col, idx) => {
        if (!col.numeric) return idx === 0 ? `Total (${totals.orderCount} orders)` : '';
        const sum = totals.sums[col.key] || 0;
        return col.reduction && sum > 0 ? `-${formatMoneyForExport(sum)}` : formatMoneyForExport(sum);
    });
}

export function orderLogRowsToExportTable(rows: OrderLogRow[], columns: OrderLogColumnDef[]): { headers: string[]; rows: (string | number)[][]; totalRow: boolean } {
    const body = rows.map(r => columns.map(c => cell(r, c)));
    const hasTotals = rows.length > 0;
    return {
        headers: columns.map(c => c.label),
        // Every Order Log export closes with the totals row the table shows. Grand Total
        // is always a column, so there is always something to total; an empty result set
        // gets no row rather than a row of zeros.
        rows: hasTotals ? [...body, totalsRow(rows, columns)] : body,
        // Sets the last row apart the way the table's footer does — bold on a tinted
        // ground. `exportTable` applies it in PDF and Excel; CSV carries no styling.
        totalRow: hasTotals,
    };
}

/** "Order Log — Cancelled Orders — Jul 24 to Aug 22" */
export function orderLogExportTitle(status: OrderLogStatus, dateRangeLabel?: string): string {
    const base = `Order Log — ${status === 'cancelled' ? 'Cancelled Orders' : 'Paid Orders'}`;
    return dateRangeLabel ? `${base} — ${dateRangeLabel}` : base;
}

/**
 * Mobile export columns for a card view (ORD-1.6). Mobile has no column picker —
 * the card view IS the column selection — so the file carries the fields the
 * selected card shows (the Financial view exports the whole waterfall), in registry order (Table No. before Guests, the financial
 * waterfall before Grand Total). Order ID and Date are always included, as they
 * are locked on desktop; the Phone column exports unmasked, as the desktop export does.
 */
const CARD_VIEW_EXPORT_KEYS: Record<'compact' | 'standard' | 'financial', string[]> = {
    compact: ['orderNo', 'orderDate', 'orderTime', 'grandTotal', 'refunded'],
    standard: ['orderNo', 'orderDate', 'orderTime', 'orderType', 'customerName', 'customerPhone', 'tableName', 'guests', 'staffName', 'itemPreview', 'cancelReason', 'refundType', 'grandTotal', 'refunded'],
    // The card shows four figures, but the file carries the full waterfall —
    // every Financial Breakdown column the desktop picker offers (added below).
    financial: ['orderNo', 'orderDate', 'orderType', 'tableName', 'guests'],
};

export function cardViewExportColumns(
    density: 'compact' | 'standard' | 'financial',
    opts: { hasTableService?: boolean; status?: OrderLogStatus; hasServiceCharge?: boolean } = {},
): OrderLogColumnDef[] {
    const keys = CARD_VIEW_EXPORT_KEYS[density] ?? CARD_VIEW_EXPORT_KEYS.standard;
    // Build with the financial group on so the Financial view's components exist
    // even for logs whose desktop table omits the waterfall.
    return buildOrderLogColumns({ ...opts, financialGroup: true })
        .filter(c => keys.indexOf(c.key) >= 0 || (density === 'financial' && c.group === 'financial'));
}
